import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Lock, Loader2, ArrowUpRight } from "lucide-react";
import api from "@/lib/api";
import { StreamPlayer } from "@/components/stream/StreamPlayer";

const LOCK = {
  sign_in: ["Sign in to watch", "This stream is for members and ticket holders.", "Sign in", "/login?next=%2Fmedia%3Ftab%3Dlive"],
  ticket_required: ["A ticket is needed to watch", "Get your ticket and the stream opens right here.", "Get a ticket", null],
  removed: ["You no longer have access", "A host has removed access to this stream.", "Contact us", "/contact"],
};

// Watch an active enhancer stream right on the Media page (same access rules as the watch page).
export const LiveViewer = ({ lives }) => {
  const [sel, setSel] = useState(null);
  const cur = lives.find((l) => l.id === sel) || lives[0];
  const { data: s, isLoading } = useQuery({
    queryKey: ["event-stream", cur.id], queryFn: () => api.get(`/events/${cur.id}/stream`).then((r) => r.data), refetchInterval: 20000, retry: false,
  });
  const lock = s && !s.can_watch ? (LOCK[s.reason] || LOCK.ticket_required) : null;
  return (
    <section className="mb-14 rounded-3xl bg-[#0F3A56] p-5 md:p-7 text-white" data-testid="media-live-viewer">
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <span className="inline-flex items-center gap-2 bg-red-600 text-white text-xs font-semibold tracking-[0.16em] uppercase rounded-full px-3 py-1">
          <span className="w-2 h-2 rounded-full bg-white animate-pulse" /> Live now
        </span>
        <h2 className="font-serif text-2xl md:text-3xl" data-testid="media-live-viewer-title">{cur.title}</h2>
        {cur.host && <span className="text-white/70 text-sm">with {cur.host}</span>}
        <Link to={cur.url} className="ml-auto text-sm text-white/80 hover:text-white inline-flex items-center gap-1" data-testid="media-live-viewer-open">
          Full watch page & chat <ArrowUpRight size={14} />
        </Link>
      </div>
      {lives.length > 1 && (
        <div className="flex flex-wrap gap-2 mb-4" data-testid="media-live-viewer-switch">
          {lives.map((l) => (
            <button key={l.id} onClick={() => setSel(l.id)} data-testid={`media-live-pick-${l.id}`}
              className={`rounded-full px-3 py-1.5 text-xs transition-colors ${l.id === cur.id ? "bg-white text-[#0F3A56]" : "bg-white/10 hover:bg-white/20"}`}>{l.title}</button>
          ))}
        </div>
      )}
      {isLoading || !s ? (
        <div className="aspect-video rounded-2xl bg-black/40 grid place-items-center"><Loader2 className="animate-spin" /></div>
      ) : lock ? (
        <div className="aspect-video rounded-2xl bg-[#144F75] grid place-items-center text-center px-6" data-testid="media-live-viewer-locked">
          <div>
            <Lock className="mx-auto mb-3" size={28} />
            <p className="font-serif text-2xl">{lock[0]}</p>
            <p className="text-white/80 mt-2 max-w-md">{lock[1]}</p>
            <Link to={lock[3] || cur.ticket_url || cur.url} className="eyw-btn !bg-white !text-[#144F75] mt-5 inline-flex" data-testid="media-live-viewer-cta">{lock[2]}</Link>
          </div>
        </div>
      ) : (
        <StreamPlayer s={s} />
      )}
    </section>
  );
};
